/**
 * Wishlist count overlay after login (session-cookie flow).
 * Hides the app's header counter and shows our own, updated from the API on login
 * and by +1/-1 when the user adds/removes items (via wk-selected on .wk-button).
 */
(function () {
  "use strict";

  /* --- Constants --- */
  var HEADER_LINK_SELECTOR = ".wishlist-header-link";
  var OVERLAY_CLASS = "mai-wishlist-count-overlay";
  var OVERLAY_ACTIVE_CLASS = "mai-wishlist-overlay-active";
  var STYLE_ID = "mai-wishlist-overlay-styles";
  var SWISH_SCRIPT_URL = "/apps/wishlist/assets/swish.js";
  var SWISH_PROXY_BASE = "/apps/wishlist";
  var API_ITEMS_PATH = "/apps/wishlist/api/items";

  /* --- State --- */
  var overlayActive = false;
  var currentOverlayCount = 0;

  /* --- Overlay: styles and DOM --- */
  function injectOverlayStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = [
      HEADER_LINK_SELECTOR + "." + OVERLAY_ACTIVE_CLASS + " .wkh-counter { visibility: hidden !important; }",
      HEADER_LINK_SELECTOR + " ." + OVERLAY_CLASS + " {",
      "  display: flex; justify-content: center; align-items: center;",
      "  min-width: var(--counter-size, 15px); height: var(--counter-size, 15px);",
      "  border-radius: calc(var(--counter-size, 15px) / 2);",
      "  font-size: 10px; line-height: 1; background: black; color: white;",
      "  position: absolute; top: 21px; right: 5px; pointer-events: none;",
      "}"
    ].join("\n");
    document.head.appendChild(style);
  }

  function ensureOverlaySetup() {
    if (overlayActive) return;
    overlayActive = true;
    injectOverlayStyles();
    document.querySelectorAll(HEADER_LINK_SELECTOR).forEach(function (link) {
      link.classList.add(OVERLAY_ACTIVE_CLASS);
      link.querySelectorAll(".wkh-button").forEach(function (btn) {
        if (btn.querySelector("." + OVERLAY_CLASS)) return;
        var overlay = document.createElement("span");
        overlay.className = OVERLAY_CLASS;
        overlay.setAttribute("aria-hidden", "true");
        overlay.textContent = "0";
        var appCounter = btn.querySelector(".wkh-counter");
        if (appCounter) btn.insertBefore(overlay, appCounter);
        else btn.appendChild(overlay);
      });
    });
  }

  function updateOverlayCount(count) {
    var num = Number(count);
    currentOverlayCount = Number.isFinite(num) && num >= 0 ? num : 0;
    var value = String(currentOverlayCount);
    var show = currentOverlayCount > 0;
    document.querySelectorAll(HEADER_LINK_SELECTOR + " ." + OVERLAY_CLASS).forEach(function (el) {
      el.textContent = value;
      el.style.opacity = show ? "1" : "0";
      el.style.visibility = show ? "visible" : "hidden";
    });
    document.querySelectorAll(HEADER_LINK_SELECTOR + " .wkh-button").forEach(function (btn) {
      if (show) btn.classList.add("wkh-selected");
      else btn.classList.remove("wkh-selected");
    });
  }

  /* --- API: derive count from response --- */
  function deriveCountFromListResponse(data) {
    if (data && data.data && typeof data.data === "object") data = data.data;
    if (Array.isArray(data)) return data.length;
    if (data && typeof data === "object") {
      if (data.wishlist && typeof data.wishlist === "object") {
        if (typeof data.wishlist.numItems === "number") return data.wishlist.numItems;
        if (Array.isArray(data.wishlist.items)) return data.wishlist.items.length;
      }
      if (typeof data.count === "number") return data.count;
      if (Array.isArray(data.items)) return data.items.length;
      if (Array.isArray(data.data)) return data.data.length;
      if (Array.isArray(data.wishlists)) {
        var n = 0;
        data.wishlists.forEach(function (w) {
          n += Array.isArray(w.items) ? w.items.length : 1;
        });
        return n;
      }
    }
    return 0;
  }

  function loadSwishScript() {
    return new Promise(function (resolve, reject) {
      if (window.swishApp || window.Swish) {
        resolve(window.swishApp || window.Swish);
        return;
      }
      var script = document.createElement("script");
      script.src = SWISH_SCRIPT_URL;
      script.async = true;
      script.onload = function () { resolve(window.swishApp || window.Swish || null); };
      script.onerror = function () { reject(new Error("Swish script failed to load")); };
      document.head.appendChild(script);
    });
  }

  function fetchWishlistCountViaProxy() {
    return fetch(API_ITEMS_PATH, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" }
    })
      .then(function (res) {
        if (!res.ok) return null;
        var ct = res.headers.get("Content-Type") || "";
        if (ct.indexOf("application/json") === -1) return null;
        return res.json();
      })
      .then(function (data) { return data != null ? deriveCountFromListResponse(data) : null; })
      .catch(function () { return null; });
  }

  /** Try SDK first, then app proxy. Returns Promise<number|null>. */
  function getWishlistCount() {
    return loadSwishScript()
      .then(function (swishApp) {
        if (typeof swishApp === "function") {
          return swishApp({ proxy: { baseUrl: SWISH_PROXY_BASE } }).then(function (swish) {
            if (swish && swish.api && swish.api.items && swish.api.items.list) {
              return swish.api.items.list().then(function (res) {
                return res && res.data != null ? deriveCountFromListResponse(res.data) : null;
              });
            }
            return null;
          });
        }
        if (swishApp && swishApp.api && swishApp.api.items && swishApp.api.items.list) {
          return swishApp.api.items.list().then(function (res) {
            return res && res.data != null ? deriveCountFromListResponse(res.data) : null;
          });
        }
        return null;
      })
      .then(function (count) { return count != null ? count : fetchWishlistCountViaProxy(); })
      .catch(function () { return fetchWishlistCountViaProxy(); });
  }

  /* --- Observer: wk-selected on product/collection buttons --- */
  function startWishlistChangeObserver() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var node = mutations[i].target;
        if (!node.classList || !node.classList.contains("wk-button")) continue;
        if (node.closest && node.closest(HEADER_LINK_SELECTOR)) continue;
        var oldVal = mutations[i].oldValue || "";
        var hadSelected = oldVal.indexOf("wk-selected") !== -1;
        var hasSelected = node.classList.contains("wk-selected");
        if (hadSelected === hasSelected) continue;
        if (hasSelected) currentOverlayCount += 1;
        else currentOverlayCount = Math.max(0, currentOverlayCount - 1);
        updateOverlayCount(currentOverlayCount);
        break;
      }
    });
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
      attributeOldValue: true
    });
  }

  /* --- Init: on login success --- */
  document.addEventListener("mai:auth:login-success", function () {
    getWishlistCount()
      .then(function (count) {
        if (count == null) return;
        ensureOverlaySetup();
        updateOverlayCount(count);
        startWishlistChangeObserver();
      })
      .catch(function () {});
  });
})();
